import { presentBotReadiness } from './botReadiness.js';

export const MARKET_DATA_ACCESS = Object.freeze({
  CONNECTED: 'CONNECTED',
  NOT_CONNECTED: 'NOT_CONNECTED',
  RECONNECT_REQUIRED: 'RECONNECT_REQUIRED',
  PROVIDER_UNAVAILABLE: 'PROVIDER_UNAVAILABLE',
});

const AUTHORIZATION_BLOCKED = new Set([
  MARKET_DATA_ACCESS.NOT_CONNECTED,
  MARKET_DATA_ACCESS.RECONNECT_REQUIRED,
]);

const ACTIVE_PREPARATION_STATES = new Set([
  'PENDING',
  'RUNNING',
  'NEEDS_ALPACA_RECONNECT',
]);

export const botRequiresMarketData = (bot, runtime) => (
  runtime?.desired_state === 'run'
  || (bot?.status === 'draft'
    && ACTIVE_PREPARATION_STATES.has(bot?.market_data_preparation?.status))
);

export const presentTodayBotReadiness = (bot, runtime, accessState) => {
  const readiness = presentBotReadiness(bot, runtime);
  if (!botRequiresMarketData(bot, runtime) || !AUTHORIZATION_BLOCKED.has(accessState)) {
    return readiness;
  }
  return {
    ...readiness,
    label: 'CONNECT ALPACA',
    tone: 'amber',
    detail: 'Connect or reconnect Alpaca Paper so this bot can receive market data.',
    needsAttention: true,
    reconnect: true,
  };
};

export const marketDataBanner = (accessState) => {
  if (accessState === MARKET_DATA_ACCESS.NOT_CONNECTED) {
    return {
      title: 'Connect Alpaca for market data',
      message: 'Connect Alpaca Paper so your bots can receive market data and Pinngro can verify the market session. Pinngro Practice remains virtual and no broker order is created.',
      cta: 'Connect Alpaca',
      destination: '/account-settings',
    };
  }
  if (accessState === MARKET_DATA_ACCESS.RECONNECT_REQUIRED) {
    return {
      title: 'Reconnect Alpaca',
      message: 'Your Alpaca connection needs to be refreshed so Pinngro can continue receiving market data. Pinngro Practice remains virtual and no broker order is created.',
      cta: 'Reconnect Alpaca',
      destination: '/account-settings',
    };
  }
  if (accessState === MARKET_DATA_ACCESS.PROVIDER_UNAVAILABLE) {
    return {
      title: 'Market data is temporarily unavailable',
      message: 'Pinngro cannot verify the market session and will not guess whether the market is open or closed. Pinngro is retrying safely. No suggestion, transaction, Alpaca Paper order, or Live order was created.',
      cta: null,
      destination: null,
    };
  }
  return null;
};

export const marketDataAttention = ({ bots, runtimes, accessState }) => {
  const demandedBots = bots.filter((bot) => botRequiresMarketData(bot, runtimes[bot.id]));
  if (demandedBots.length === 0) return null;

  const runtimeAccessState = (bot) => runtimes[bot.id]?.market_watch?.market_data_access_state;
  const reconnectBots = demandedBots.filter((bot) => (
    runtimeAccessState(bot) === MARKET_DATA_ACCESS.RECONNECT_REQUIRED
  ));
  const disconnectedBots = demandedBots.filter((bot) => (
    runtimeAccessState(bot) === MARKET_DATA_ACCESS.NOT_CONNECTED
  ));

  if (accessState === MARKET_DATA_ACCESS.NOT_CONNECTED || disconnectedBots.length > 0) {
    return {
      kind: MARKET_DATA_ACCESS.NOT_CONNECTED,
      title: 'Connect Alpaca for market data',
      message: 'Connect Alpaca Paper so your bots can receive market data and Pinngro can verify the market session. Pinngro Practice remains virtual and no Paper or Live broker order is created.',
      cta: 'Connect Alpaca',
      destination: '/account-settings',
      affectedBots: accessState === MARKET_DATA_ACCESS.NOT_CONNECTED
        ? demandedBots : disconnectedBots,
    };
  }

  if (accessState === MARKET_DATA_ACCESS.RECONNECT_REQUIRED || reconnectBots.length > 0) {
    return {
      kind: MARKET_DATA_ACCESS.RECONNECT_REQUIRED,
      title: 'Reconnect Alpaca',
      message: 'Your Alpaca connection needs to be refreshed so Pinngro can continue receiving market data. Pinngro Practice remains virtual and no Paper or Live broker order is created.',
      cta: 'Reconnect Alpaca',
      destination: '/account-settings',
      affectedBots: accessState === MARKET_DATA_ACCESS.RECONNECT_REQUIRED
        ? demandedBots : reconnectBots,
    };
  }

  const providerUnavailableBots = demandedBots.filter((bot) => (
    runtimeAccessState(bot) === MARKET_DATA_ACCESS.PROVIDER_UNAVAILABLE
  ));
  if (
    accessState === MARKET_DATA_ACCESS.PROVIDER_UNAVAILABLE
    || providerUnavailableBots.length > 0
  ) {
    const affectedBots = demandedBots.filter((bot) => (
      presentBotReadiness(bot, runtimes[bot.id]).label === 'NEEDS ATTENTION'
      && (
        accessState === MARKET_DATA_ACCESS.PROVIDER_UNAVAILABLE
        || providerUnavailableBots.some((affected) => affected.id === bot.id)
      )
    ));
    if (affectedBots.length === 0) return null;
    return {
      kind: MARKET_DATA_ACCESS.PROVIDER_UNAVAILABLE,
      title: 'Market data needs attention',
      message: `Pinngro is not receiving reliable market data for ${affectedBots.length === 1 ? 'this bot' : 'these bots'}. Review the bot report for details. Nothing executed, and no Alpaca Paper or Live order was created.`,
      cta: 'View bot report',
      destination: `/bots/${affectedBots[0].id}/report`,
      affectedBots,
    };
  }

  return null;
};
